import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDisableScrollSnap, useModal } from "../../util/hook";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";

import styled from "styled-components";
import { SermonContentStyle } from "../../style/SermonContentStyles";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSermon } from "../../util/hook";
import Header from "../organisms/Header";
import Footer from "../organisms/Footer";
import SermonView from "../organisms/SermonView";
import ScriptureSelector from "../molecules/ScriptureSelector";

const categories = [
  { value: "sunday", label: "주일예배" },
  { value: "friday", label: "금요모임" },
  { value: "special", label: "특별집회" },
  { value: "seasonal", label: "절기" },
];

const Editor = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addSermon, getSermon, updateSermon } = useSermon();
  const { showAlert } = useModal();
  useDisableScrollSnap();

  const isEditMode = !!id;

  const [isPreview, setIsPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("sunday");
  const [scriptures, setScriptures] = useState<string[]>([""]);
  const [noScripture, setNoScripture] = useState(false);
  const [date, setDate] = useState("");
  const [preacher, setPreacher] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [content, setContent] = useState("");

  // 원본 데이터 저장 (수정 모드에서 변경 여부 확인용)
  const originalData = useRef<{
    title: string;
    category: string;
    scripture: string[];
    date: string;
    preacher: string;
    youtubeUrl: string;
    content: string;
  } | null>(null);

  useEffect(() => {
    const initializeEditor = async () => {
      if (isEditMode) {
        const sermon = getSermon(Number(id));
        if (sermon) {
          setTitle(sermon.title);
          setCategory(sermon.category);
          // scripture가 이제 배열이므로 직접 사용
          const scriptureArray = sermon.scripture && sermon.scripture.length > 0
            ? sermon.scripture
            : [""];
          setScriptures(scriptureArray);
          setNoScripture(!sermon.scripture || sermon.scripture.length === 0);
          setDate(sermon.date);
          setPreacher(sermon.preacher);
          setYoutubeUrl(sermon.youtubeUrl || "");
          setContent(sermon.content || "");

          // 원본 데이터 저장
          originalData.current = {
            title: sermon.title,
            category: sermon.category,
            scripture: sermon.scripture || [],
            date: sermon.date,
            preacher: sermon.preacher,
            youtubeUrl: sermon.youtubeUrl || "",
            content: sermon.content || "",
          };
        } else {
          await showAlert("존재하지 않는 설교입니다.");
          navigate(`/sermon/${id}`);
        }
      } else {
        // Initial date setting for new post
        const today = new Date();
        const formattedDate = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, "0")}.${String(today.getDate()).padStart(2, "0")}`;
        setDate(formattedDate);
      }
    };

    initializeEditor();
  }, [id, isEditMode, getSermon, navigate, showAlert]);

  const modules = React.useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [
          { list: "ordered" },
          { list: "bullet" },
          { indent: "-1" },
          { indent: "+1" },
        ],
        ["link"],
        [
          { align: [] },
          {
            color: [
              "#000000",
              "#e60000",
              "#ff9900",
              "#ffff00",
              "#008a00",
              "#0066cc",
              "#9933ff",
              "#ffffff",
              "#facccc",
              "#ffebcc",
              "#ffffcc",
              "#cce8cc",
              "#cce0f5",
              "#ebd6ff",
              "#bbbbbb",
              "#f06666",
              "#ffc266",
              "#ffff66",
              "#66b966",
              "#66a3e0",
              "#c285ff",
              "#888888",
              "#a10000",
              "#b26b00",
              "#b2b200",
              "#006100",
              "#0047b2",
              "#6b24b2",
              "#444444",
              "#5c0000",
              "#663d00",
              "#666600",
              "#003700",
              "#002966",
              "#3d1466",
            ],
          },
          { background: [] },
        ],
        ["clean"],
      ],
    }),
    [],
  );

  // 특정 인덱스의 scripture 변경
  const handleScriptureChange = useCallback((index: number, value: string) => {
    setScriptures(prev => {
      const newScriptures = [...prev];
      newScriptures[index] = value;
      return newScriptures;
    });
  }, []);

  // scripture 추가
  const addScripture = useCallback(() => {
    setScriptures(prev => [...prev, ""]);
  }, []);

  // scripture 삭제
  const removeScripture = useCallback((index: number) => {
    setScriptures(prev => {
      if (prev.length <= 1) return [""]; // 최소 1개는 유지
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // 날짜 형식 변환: "YYYY.MM.DD" -> "YYYY-MM-DD" (input date용)
  const dateToInputFormat = (dateStr: string): string => {
    if (!dateStr) return "";
    return dateStr.replace(/\./g, "-");
  };

  // 날짜 형식 변환: "YYYY-MM-DD" -> "YYYY.MM.DD" (표시용)
  const inputToDisplayFormat = (dateStr: string): string => {
    if (!dateStr) return "";
    return dateStr.replace(/-/g, ".");
  };

  const hasChanges = (): boolean => {
    if (!originalData.current) return true;

    // scriptures 배열 비교 (빈 문자열 제거 후 비교)
    const currentScriptures = scriptures.filter(s => s.trim());
    const originalScriptures = originalData.current.scripture.filter(s => s.trim());
    const scripturesChanged = 
      currentScriptures.length !== originalScriptures.length ||
      currentScriptures.some((s, i) => s !== originalScriptures[i]);
    
    return (
      title !== originalData.current.title ||
      category !== originalData.current.category ||
      scripturesChanged ||
      date !== originalData.current.date ||
      preacher !== originalData.current.preacher ||
      youtubeUrl !== originalData.current.youtubeUrl ||
      content !== originalData.current.content
    );
  };

  // HTML 내용이 실제로 비어있는지 체크 (빈 태그만 있는 경우 포함)
  const isContentEmpty = (html: string): boolean => {
    if (!html) return true;
    // HTML 태그 제거 후 공백 제거
    const text = html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    return text.length === 0;
  };

  // 빈 HTML을 빈 문자열로 정리
  const cleanContent = (html: string): string => {
    return isContentEmpty(html) ? "" : html;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title) {
      await showAlert("제목은 필수입니다.");
      return;
    }

    // 수정 모드에서 변경 사항이 없으면 서버 통신 없이 이동
    if (isEditMode && !hasChanges()) {
      await showAlert("변경된 내용이 없습니다.");
      navigate(`/sermon/${id}`);
      return;
    }

    setSubmitting(true);

    try {
      const categoryObj = categories.find((c) => c.value === category);
      const categoryName = categoryObj ? categoryObj.label : "";

      // 빈 scripture 제거
      const scriptureArray = scriptures.filter(s => s.trim());

      const sermonData = {
        category,
        categoryName,
        title,
        scripture: scriptureArray,
        date,
        preacher,
        youtubeUrl,
        content: cleanContent(content),
      };

      if (isEditMode) {
        await updateSermon(Number(id), sermonData);
        await showAlert("수정되었습니다.");
        navigate(`/sermon/${id}`, {
          replace: true,
          state: { backTo: location.state?.backTo },
        });
      } else {
        await addSermon(sermonData);
        await showAlert("등록되었습니다.");
        navigate("/", { replace: true });
      }
    } catch (error) {
      console.error("Error saving sermon:", error);
      await showAlert("저장 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setSubmitting(false);
    }
  };

  const togglePreview = () => {
    setIsPreview(!isPreview);
  };

  // Preview Data
  const categoryObj = categories.find((c) => c.value === category);
  const scriptureArray = scriptures.filter(s => s.trim());
  const previewData = {
    id: 0, // Mock ID
    category,
    categoryName: categoryObj ? categoryObj.label : "주일예배",
    title: title || "제목 미리보기",
    scripture: scriptureArray.length > 0 ? scriptureArray : ["본문 미리보기"],
    date: date || "날짜 미리보기",
    preacher: preacher || "설교자 미리보기",
    youtubeUrl: youtubeUrl || undefined,
    content: content || "<p>내용 미리보기</p>",
  };

  if (isPreview) {
    return (
      <>
        <Header />
        <Container>
          <PageTitle>미리보기</PageTitle>
          <SermonView sermon={previewData} />
          <ButtonGroup>
            <Button onClick={togglePreview} className="cancel" type="button">
              돌아가기
            </Button>
          </ButtonGroup>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <Container>
        <PageTitle>{isEditMode ? "설교 수정" : "설교 작성"}</PageTitle>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>카테고리</Label>
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {categories.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>제목</Label>
            <Input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="설교 제목을 입력하세요"
            />
          </FormGroup>

          <FormGroup>
            <LabelRow>
              <Label>본문 말씀</Label>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={noScripture}
                  onChange={(e) => {
                    setNoScripture(e.target.checked);
                    if (e.target.checked) {
                      setScriptures([""]);
                    }
                  }}
                />
                본문 말씀 없음
              </CheckboxLabel>
            </LabelRow>
            {!noScripture && (
              <>
                {scriptures.map((scripture, index) => (
                  <ScriptureItem key={index}>
                    <ScriptureSelector
                      value={scripture}
                      onChange={(value) => handleScriptureChange(index, value)}
                    />
                    {scriptures.length > 1 && (
                      <RemoveButton
                        type="button"
                        onClick={() => removeScripture(index)}
                        title="삭제"
                      >
                        ✕
                      </RemoveButton>
                    )}
                  </ScriptureItem>
                ))}
                <AddScriptureButton type="button" onClick={addScripture}>
                  + 본문 말씀 추가
                </AddScriptureButton>
                {scriptures.some(s => s.trim()) && (
                  <ScripturePreview>
                    선택된 본문: {scriptures.filter(s => s.trim()).join(" / ")}
                  </ScripturePreview>
                )}
              </>
            )}
          </FormGroup>

          <Row>
            <FormGroup style={{ width: "180px" }}>
              <Label>날짜</Label>
              <Input
                type="date"
                value={dateToInputFormat(date)}
                onChange={(e) => setDate(inputToDisplayFormat(e.target.value))}
              />
            </FormGroup>
            <FormGroup style={{ width: "150px" }}>
              <Label>설교자</Label>
              <Input
                type="text"
                value={preacher}
                onChange={(e) => setPreacher(e.target.value)}
                placeholder="이름"
              />
            </FormGroup>
          </Row>

          <FormGroup>
            <Label>유튜브 링크 (선택)</Label>
            <Input
              type="url"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
            />
          </FormGroup>

          <EditorWrapper>
            <Label>내용</Label>
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              modules={modules}
              style={{ height: "400px", marginBottom: "50px" }}
            />
          </EditorWrapper>

          <ButtonGroup>
            <Button
              type="button"
              onClick={() =>
                navigate(isEditMode ? `/sermon/${id}` : "/", {
                  replace: isEditMode,
                  state: isEditMode
                    ? { backTo: location.state?.backTo }
                    : undefined,
                })
              }
              className="cancel"
            >
              취소
            </Button>
            <Button
              type="button"
              onClick={togglePreview}
              className="preview"
              disabled={submitting}
            >
              미리보기
            </Button>
            <Button
              type="submit"
              className="submit"
              disabled={submitting || (isEditMode && !hasChanges())}
            >
              {submitting
                ? "저장 중..."
                : isEditMode
                  ? "수정 완료"
                  : "작성 완료"}
            </Button>
          </ButtonGroup>
        </Form>
      </Container>
      <Footer />
    </>
  );
};

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 60px 20px;
  @media (max-width: 768px) {
    padding: 60px 5px;
  }
`;

const PageTitle = styled.h2`
  font-size: 28px;
  color: #333;
  margin-bottom: 40px;
  text-align: center;
  font-weight: 700;
`;

const Form = styled.form`
  background: #fff;
  padding: 40px;
  border: 1px solid #ddd;
  border-radius: 8px;

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 4px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Row = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  font-weight: 700;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  ${Label} {
    margin-bottom: 0;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  user-select: none;

  &:hover {
    color: #004094;
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #004094;
`;

const Input = styled.input`
  width: 100%;
  height: 44px;
  padding: 0 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  box-sizing: border-box;
  background: white;
  color: #333;
  font-family: inherit;

  &:focus {
    border-color: #004094;
    outline: none;
  }

  /* Date input 스타일 통일 */
  &[type="date"] {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;

    &::-webkit-calendar-picker-indicator {
      cursor: pointer;
      opacity: 0.6;

      &:hover {
        opacity: 1;
      }
    }

    &::-webkit-date-and-time-value {
      text-align: left;
    }
  }
`;

const Select = styled.select`
  width: 100%;
  height: 44px;
  padding: 0 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  box-sizing: border-box;

  &:focus {
    border-color: #004094;
    outline: none;
  }
`;

const ScripturePreview = styled.div`
  margin-top: 12px;
  padding: 10px 15px;
  background: #f0f4ff;
  border-radius: 4px;
  font-size: 14px;
  color: #004094;
  font-weight: 600;
`;

const ScriptureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const RemoveButton = styled.button`
  width: 32px;
  height: 44px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  color: #999;
  font-size: 16px;
  cursor: pointer;
  flex-shrink: 0;
  transition: 0.2s;

  &:hover {
    background: #fee;
    border-color: #f44336;
    color: #f44336;
  }
`;

const AddScriptureButton = styled.button`
  width: 100%;
  padding: 10px;
  border: 1px dashed #004094;
  border-radius: 4px;
  background: transparent;
  color: #004094;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;
  margin-top: 8px;

  &:hover {
    background: #f0f4ff;
  }
`;

const EditorWrapper = styled.div`
  margin-bottom: 80px; /* 툴바 등 고려하여 여백 확보 */

  .ql-editor {
    ${SermonContentStyle}
    min-height: 350px; /* 에디터 최소 높이 확보 */
  }

  @media (max-width: 768px) {
    margin-bottom: 60px;
  }

  @media (max-width: 480px) {
    margin-bottom: 120px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 30px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    margin-top: 40px;
  }
`;

const Button = styled.button`
  padding: 15px 40px;
  font-size: 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
  transition: 0.3s;
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: 12px 25px;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 15px 20px;
    text-align: center;
  }

  &.submit {
    background: #004094;
    color: #fff;
    border: none;

    &:hover {
      background: #003070;
    }
  }

  &.cancel {
    background: #f5f5f5;
    color: #666;
    border: 1px solid #ddd;

    &:hover {
      background: #e0e0e0;
    }
  }

  &.preview {
    background: #ff9800;
    color: #fff;
    border: none;

    &:hover {
      background: #f57c00;
    }
  }

  &:disabled {
    background: #ccc;
    color: #888;
    cursor: not-allowed;
    border: none;

    &:hover {
      background: #ccc;
    }
  }
`;

export default Editor;
